// Service de synchronisation entre les modules locaux et l'API

import { ModulesApiService } from './ModulesApiService';
import type { ApiModule, CreateModuleDto, SyncResult } from './types';
import type { ComponentMetadata } from '../Core/types';
import { getAllComponents } from '../Core/ComponentRegistry';
import type { ComponentParams } from '../Config/config';

// Re-export pour compatibilité
export type { SyncResult };

export class ModuleSyncService {
    private apiService: ModulesApiService;

    constructor(apiService: ModulesApiService) {
        this.apiService = apiService;
    }

    /**
     * Compare les modules locaux avec ceux de l'API
     */
    async compareModules(): Promise<SyncResult> {
        const result: SyncResult = {
            created: [],
            updated: [],
            deleted: [],
            errors: [],
            modulesToUpdate: [],
            modulesToCreate: [],
            modulesToDelete: []
        };

        try {
            // Récupérer les modules de l'API
            const apiModules = await this.apiService.getAllModules();
            
            // Récupérer les modules locaux
            const localModules = getAllComponents();

            // Créer des maps pour faciliter la comparaison
            const apiModulesMap = new Map(apiModules.map(m => [m.name, m]));
            const localModulesMap = new Map(localModules.map(m => [m.name, m]));

            // Identifier les modules à créer (présents localement mais pas dans l'API)
            for (const [name, localModule] of localModulesMap) {
                if (!apiModulesMap.has(name)) {
                    result.modulesToCreate.push(name);
                }
            }

            // Identifier les modules à mettre à jour (présents des deux côtés mais différents)
            for (const [name, apiModule] of apiModulesMap) {
                const localModule = localModulesMap.get(name);
                if (localModule && this.shouldUpdateModule(apiModule, localModule)) {
                    result.modulesToUpdate.push(apiModule);
                }
            }

            // Identifier les modules à supprimer (présents dans l'API mais pas utilisés localement)
            for (const [name, apiModule] of apiModulesMap) {
                if (!localModulesMap.has(name)) {
                    result.modulesToDelete.push(name);
                }
            }

        } catch (error) {
            result.errors.push(`Erreur lors de la comparaison: ${error instanceof Error ? error.message : String(error)}`);
        }

        return result;
    }

    /**
     * Synchronise les modules locaux avec l'API
     */
    async syncModules(usedModules: string[]): Promise<SyncResult> {
        const result: SyncResult = {
            created: [],
            updated: [],
            deleted: [],
            errors: [],
            modulesToUpdate: [],
            modulesToCreate: [],
            modulesToDelete: []
        };

        try {
            // Récupérer les modules de l'API
            const apiModules = await this.apiService.getAllModules();
            
            // Récupérer les modules locaux
            const localModules = getAllComponents();

            // Créer des maps
            const apiModulesMap = new Map(apiModules.map(m => [m.name, m]));
            const localModulesMap = new Map(localModules.map(m => [m.name, m]));
            const usedModulesSet = new Set(usedModules);

            // Créer les modules manquants
            for (const [name, localModule] of localModulesMap) {
                if (!apiModulesMap.has(name)) {
                    try {
                        const createDto = this.convertLocalToCreateDto(localModule);
                        await this.apiService.createModule(createDto);
                        result.created.push(name);
                    } catch (error) {
                        result.errors.push(`Erreur lors de la création de ${name}: ${error instanceof Error ? error.message : String(error)}`);
                    }
                }
            }

            // Mettre à jour les modules existants
            for (const [name, apiModule] of apiModulesMap) {
                const localModule = localModulesMap.get(name);
                if (localModule && this.shouldUpdateModule(apiModule, localModule)) {
                    try {
                        const updateDto = this.convertLocalToUpdateDto(localModule);
                        await this.apiService.updateModule(apiModule.id, updateDto);
                        result.updated.push(name);
                    } catch (error) {
                        result.errors.push(`Erreur lors de la mise à jour de ${name}: ${error instanceof Error ? error.message : String(error)}`);
                    }
                }
            }

            // Supprimer les modules non utilisés
            for (const [name, apiModule] of apiModulesMap) {
                if (!usedModulesSet.has(name) && !localModulesMap.has(name)) {
                    try {
                        await this.apiService.deleteModule(apiModule.id);
                        result.deleted.push(name);
                    } catch (error) {
                        result.errors.push(`Erreur lors de la suppression de ${name}: ${error instanceof Error ? error.message : String(error)}`);
                    }
                }
            }

        } catch (error) {
            result.errors.push(`Erreur générale: ${error instanceof Error ? error.message : String(error)}`);
        }

        return result;
    }

    /**
     * Charge les paramètres depuis l'API et les fusionne avec les paramètres locaux
     */
    async loadParametersFromApi(localParams: ComponentParams): Promise<ComponentParams> {
        try {
            const apiModules = await this.apiService.getAllModules();
            const mergedParams: ComponentParams = { ...localParams };

            for (const apiModule of apiModules) {
                if (!mergedParams[apiModule.name]) {
                    mergedParams[apiModule.name] = {};
                }

                // Fusionner les paramètres de l'API avec les paramètres locaux
                for (const param of apiModule.parameters) {
                    // Si le paramètre n'existe pas localement, utiliser la valeur par défaut de l'API
                    if (mergedParams[apiModule.name][param.key] === undefined && param.default_value !== undefined) {
                        mergedParams[apiModule.name][param.key] = this.parseParameterValue(param.default_value, param.type);
                    }
                }
            }

            return mergedParams;
        } catch (error) {
            console.error('Erreur lors du chargement des paramètres:', error);
            return localParams;
        }
    }

    /**
     * Détermine si un module doit être mis à jour
     */
    private shouldUpdateModule(apiModule: ApiModule, localModule: ComponentMetadata): boolean {
        return (
            apiModule.display_name !== localModule.displayName ||
            apiModule.description !== localModule.description ||
            apiModule.category !== localModule.category
        );
    }

    /**
     * Convertit un module local en DTO de création
     */
    private convertLocalToCreateDto(localModule: ComponentMetadata): CreateModuleDto {
        return {
            name: localModule.name,
            display_name: localModule.displayName,
            description: localModule.description,
            category: localModule.category,
            component_code: `// Module: ${localModule.name}\n// Géré localement dans le projet`, // Code placeholder
            parameters: Object.entries(localModule.paramDefinitions).map(([key, def]) => ({
                key,
                type: this.mapParamType(def.type),
                label: def.label,
                description: def.description,
                default_value: String(localModule.defaultParams[key] ?? ''),
                required: false
            }))
        };
    }

    /**
     * Convertit un module local en DTO de mise à jour
     */
    private convertLocalToUpdateDto(localModule: ComponentMetadata) {
        return {
            display_name: localModule.displayName,
            description: localModule.description,
            component_code: `// Module: ${localModule.name}\n// Géré localement dans le projet`,
            parameters: Object.entries(localModule.paramDefinitions).map(([key, def]) => ({
                key,
                type: this.mapParamType(def.type),
                label: def.label,
                description: def.description,
                default_value: String(localModule.defaultParams[key] ?? ''),
                required: false
            }))
        };
    }

    /**
     * Mappe le type de paramètre local vers le type de l'API
     */
    private mapParamType(type: string): 'video' | 'string' | 'number' | 'boolean' {
        if (type === 'number') return 'number';
        if (type === 'boolean') return 'boolean';
        return 'string';
    }

    /**
     * Parse la valeur d'un paramètre selon son type
     */
    private parseParameterValue(value: string, type: string): any {
        switch (type) {
            case 'number':
                return Number(value);
            case 'boolean':
                return value === 'true' || value === '1';
            default:
                return value;
        }
    }
}

